import React, { useEffect, useMemo, useRef } from 'react';
import { RootState, useAppSelector, useAppDispatch, setSubscriberId, onRemoteUpdate } from '../../redux';
import { maybeShowDesktopNotification } from '../../utils/desktopNotifications';
import {
    createNotificationInboxChannel,
    isNotificationInboxEvent,
    publishNotificationInboxUpdate,
} from '../../utils/notificationInboxSync';
import { v4 as uuidv4 } from 'uuid';

interface MercureProps {
    hub: string;
    topics: string[];
    onUpdate?: (update: AnyObj) => void;
    children?: JSX.Element | JSX.Element[];
}

const Mercure = (props: MercureProps) => {
    const { onUpdate, hub, topics, children } = props;

    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state : RootState) => state.auth);
    const userRef = useRef(user);
    userRef.current = user;
    // Stable key so a new topics array with the same values does not reconnect.
    const topicsKey = useMemo(() => topics.join('\0'), [topics]);

    useEffect(() => {
        const subscriberId = uuidv4();
        dispatch(setSubscriberId(subscriberId));

        const url = new URL(hub);
        topicsKey.split('\0').filter(Boolean).forEach(topic => {
            url.searchParams.append('topic', topic);
        });

        const inboxChannel = createNotificationInboxChannel();

        // Mercure dev hub allows anonymous subscribers; credentials are not required.
        // This avoids browser CORS rejection when hub responds with wildcard origin.
        const eventSource     = new EventSource(url);
        eventSource.onmessage = (e: AnyObj) => {
            const payload = JSON.parse(e.data) || {};
            const currentUser = userRef.current;

            // ignore notifications for our own updates
            if (payload.source === subscriberId)
                return;

            // do not consider other users time tracking entries
            const { data, type } = payload;
            if (type.match(/track\./) && data.author?._id !== currentUser?._id)
                return;

            // Inbox notifications are published on a user topic, but the app
            // currently subscribes to "*". Ignore other users' notification events.
            if (isNotificationInboxEvent(type)) {
                if (String(data?.user || '') !== String(currentUser?._id || ''))
                    return;

                if (type === 'notification.created')
                    maybeShowDesktopNotification(currentUser, data);

                publishNotificationInboxUpdate(inboxChannel, { type, data });
            }

            dispatch(onRemoteUpdate(payload));
            onUpdate && onUpdate(payload);
        };

        return () => {
            eventSource.close();
            inboxChannel?.close();
        };
    }, [dispatch, hub, onUpdate, topicsKey, user?._id]);

    return <React.Fragment>{children}</React.Fragment>;
}

export default Mercure;
